import { useEffect, useRef } from "react";

const COLORS = ["255,255,255", "228,228,228", "190,190,190", "140,140,140"];

// Decorative only: abstract flow field, no data or "analysis" implied.
export default function IntelligenceField({ className = "" }) {
  const wrapRef = useRef(null);
  const trailRef = useRef(null);
  const netRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const tc = trailRef.current;
    const nc = netRef.current;
    const tx = tc.getContext("2d");
    const nx = nc.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mobile = window.matchMedia("(max-width: 767px)").matches;
    const finePointer = window.matchMedia("(pointer: fine)").matches && !mobile;
    const COUNT = mobile ? 90 : 260;
    const NODES = mobile ? 9 : 15;
    const SQ = 0.62;
    let w = 0, h = 0, cx = 0, cy = 0, R = 0, t = 0, ox = 0, oy = 0;
    let raf = 0, inView = true, entryStart = 0;
    const ENTRY_MS = mobile ? 1400 : 1800;
    const clamp01 = (v) => Math.max(0, Math.min(1, v));
    const pointer = { x: 0, y: 0, active: false };
    let parts = [];
    let nodes = [];

    const spawn = (p) => {
      const a = Math.random() * Math.PI * 2;
      const ring = R * (0.3 + Math.random() * 0.75);
      p.x = cx + ox + Math.cos(a) * ring;
      p.y = cy + oy + Math.sin(a) * ring * SQ;
      p.ring = ring;
      p.life = 0;
      p.max = 140 + Math.random() * 260;
      p.speed = 0.6 + Math.random() * 1.1;
      p.dir = Math.random() > 0.18 ? 1 : -1;
      p.c = COLORS[(Math.random() * COLORS.length) | 0];
      p.lw = Math.random() > 0.88 ? 1.8 : 0.9;
      return p;
    };

    const resize = () => {
      const r = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2);
      w = r.width;
      h = r.height;
      [tc, nc].forEach((c) => {
        c.width = w * dpr;
        c.height = h * dpr;
        c.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0);
      });
      cx = w * 0.5;
      cy = mobile ? h * 0.36 : h * 0.46;
      R = Math.min(w * (mobile ? 0.62 : 0.4), h * (mobile ? 0.34 : 0.62));
      parts = Array.from({ length: COUNT }, () => spawn({}));
      parts.forEach((p) => { p.life = Math.random() * p.max; });
      nodes = Array.from({ length: NODES }, (_, i) => ({
        a: (i / NODES) * Math.PI * 2 + Math.random() * 0.4,
        r: R * (0.25 + Math.random() * 0.8),
        s: (0.0008 + Math.random() * 0.0016) * (Math.random() > 0.5 ? 1 : -1),
        ph: Math.random() * 10,
        sa: Math.random() * Math.PI * 2,
        sd: R * (0.3 + Math.random() * 0.45),
      }));
    };

    const step = () => {
      t += 1;
      if (!entryStart) entryStart = performance.now();
      const raw = reduce ? 1 : clamp01((performance.now() - entryStart) / ENTRY_MS);
      const e = 1 - (1 - raw) ** 3;
      const linkFade = 1 - (1 - clamp01((raw - 0.35) / 0.65)) ** 2;
      const tox = pointer.active ? (pointer.x - cx) * 0.06 : 0;
      const toy = pointer.active ? (pointer.y - cy) * 0.06 : 0;
      ox += (tox - ox) * 0.03;
      oy += (toy - oy) * 0.03;
      const ccx = cx + ox, ccy = cy + oy;

      tx.globalCompositeOperation = "destination-out";
      tx.fillStyle = "rgba(0,0,0,0.05)";
      tx.fillRect(0, 0, w, h);
      tx.globalCompositeOperation = "lighter";

      for (const p of parts) {
        const dx = p.x - ccx, dy = (p.y - ccy) / SQ;
        const d = Math.hypot(dx, dy) || 1;
        const ang = Math.atan2(dy, dx);
        const n = Math.sin(p.x * 0.006 + t * 0.006) + Math.cos(p.y * 0.007 - t * 0.004);
        const pull = (p.ring - d) * 0.006;
        let vx = -Math.sin(ang) * p.dir + Math.cos(ang) * pull + Math.cos(n * Math.PI) * 0.32;
        let vy = (Math.cos(ang) * p.dir + Math.sin(ang) * pull) * SQ + Math.sin(n * Math.PI) * 0.32;
        if (pointer.active) {
          const px = p.x - pointer.x, py = p.y - pointer.y;
          const pd = Math.hypot(px, py);
          if (pd < 140) { vx += (px / pd) * (1 - pd / 140) * 1.6; vy += (py / pd) * (1 - pd / 140) * 1.6; }
        }
        const nxp = p.x + vx * p.speed, nyp = p.y + vy * p.speed;
        const alpha = Math.sin((Math.PI * p.life) / p.max) * 0.55 * (0.25 + 0.75 * e);
        tx.strokeStyle = `rgba(${p.c},${alpha.toFixed(3)})`;
        tx.lineWidth = p.lw;
        tx.beginPath();
        tx.moveTo(p.x, p.y);
        tx.lineTo(nxp, nyp);
        tx.stroke();
        p.x = nxp; p.y = nyp; p.life += 1;
        if (p.life > p.max) spawn(p);
      }

      nx.clearRect(0, 0, w, h);
      const pts = nodes.map((nd) => {
        nd.a += nd.s;
        const k = 1 - e;
        return [
          ccx + Math.cos(nd.a) * nd.r + Math.cos(nd.sa) * nd.sd * k,
          ccy + Math.sin(nd.a) * nd.r * SQ + Math.sin(t * 0.01 + nd.ph) * 6 + Math.sin(nd.sa) * nd.sd * SQ * k,
        ];
      });
      const maxD = R * 0.75;
      nx.lineWidth = 0.7;
      for (let i = 0; linkFade > 0 && i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1]);
          if (d < maxD) {
            nx.strokeStyle = `rgba(220,220,220,${((1 - d / maxD) * 0.18 * linkFade).toFixed(3)})`;
            nx.beginPath();
            nx.moveTo(pts[i][0], pts[i][1]);
            nx.lineTo(pts[j][0], pts[j][1]);
            nx.stroke();
          }
        }
      }
      pts.forEach(([x, y], i) => {
        const pulse = (0.55 + Math.sin(t * 0.02 + nodes[i].ph) * 0.45) * (0.3 + 0.7 * e);
        const g = nx.createRadialGradient(x, y, 0, x, y, 14);
        g.addColorStop(0, `rgba(235,235,235,${(0.4 * pulse).toFixed(3)})`);
        g.addColorStop(1, "rgba(235,235,235,0)");
        nx.fillStyle = g;
        nx.beginPath(); nx.arc(x, y, 14, 0, Math.PI * 2); nx.fill();
        nx.fillStyle = `rgba(255,255,255,${(0.9 * (0.35 + 0.65 * e)).toFixed(3)})`;
        nx.beginPath(); nx.arc(x, y, 1.4, 0, Math.PI * 2); nx.fill();
      });
    };

    const loop = () => { step(); raf = requestAnimationFrame(loop); };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    const start = () => { if (!raf && inView && !document.hidden && !reduce) raf = requestAnimationFrame(loop); };
    const renderStatic = () => { for (let i = 0; i < 320; i++) step(); };

    resize();
    if (reduce) renderStatic(); else start();

    const ro = new ResizeObserver(() => { resize(); if (reduce) renderStatic(); });
    ro.observe(wrap);
    const io = new IntersectionObserver(([e]) => { inView = e.isIntersecting; inView ? start() : stop(); });
    io.observe(wrap);
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener("visibilitychange", onVis);
    const onMove = (e) => {
      const r = wrap.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
      pointer.active = pointer.y >= 0 && pointer.y <= r.height;
    };
    const onLeave = () => { pointer.active = false; };
    if (finePointer && !reduce) {
      window.addEventListener("pointermove", onMove, { passive: true });
      document.addEventListener("pointerleave", onLeave);
    }
    return () => {
      stop(); ro.disconnect(); io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <div ref={wrapRef} aria-hidden className={`pointer-events-none absolute inset-0 ${className}`}>
      <canvas ref={trailRef} className="absolute inset-0 h-full w-full" />
      <canvas ref={netRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
